import { useCallback, useRef, useState } from 'react'
import {
  type RiskReductionMeasure,
  type RrmGenerationStreamEvent,
  useCreateRiskReductionMeasure,
  useDeleteRiskReductionMeasure,
  useExportSrsPdf,
  useListRiskReductionMeasures,
  useListSafetyFunctions,
  useStartRrmGeneration,
  useUpdateRiskReductionMeasure,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import {
  ChevronDown,
  File,
  Download,
  Loader2,
  SparklesIcon,
  ShieldCheck,
  PlusIcon,
  AlertTriangle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { DeleteRiskReductionMeasureDialog } from './DeleteRiskReductionMeasureDialog'
import { AssignSafetyFunctionDialog } from './AssignSafetyFunctionDialog'
import { GenerateRrmsFromStandardsModal } from './GenerateRrmsFromStandardsModal'
import { RiskReductionMeasureCard } from './RiskReductionMeasureCard'
import { RiskReductionMeasureDialog } from './RiskReductionMeasureDialog'
import {
  type RiskReductionMeasureFormData,
} from './RiskReductionMeasureForm.schema'
import { toRiskReductionMeasureInput, toSafetyFunctionAssignmentInput } from './riskReductionMeasuresUtils'

export function RiskReductionMeasuresPage() {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const params = useParams<{ machineryDirectiveComplianceId: string }>()
  const complianceId = params.machineryDirectiveComplianceId!

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingRiskReductionMeasure, setEditingRiskReductionMeasure] =
    useState<RiskReductionMeasure | null>(null)
  const [deletingRiskReductionMeasureId, setDeletingRiskReductionMeasureId] =
    useState<string | null>(null)
  const [assigningRiskReductionMeasure, setAssigningRiskReductionMeasure] =
    useState<RiskReductionMeasure | null>(null)
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false)
  const [generationError, setGenerationError] = useState<string | null>(null)
  const [progressMessage, setProgressMessage] = useState<string | null>(null)
  const [progressCurrent, setProgressCurrent] = useState<number | null>(null)
  const [progressTotal, setProgressTotal] = useState<number | null>(null)
  const generationProgressCurrentRef = useRef<number | null>(null)
  const generationProgressTotalRef = useRef<number | null>(null)

  const {
    data: riskReductionMeasures = [],
    isLoading,
    error,
  } = useListRiskReductionMeasures(complianceId)
  const { data: safetyFunctions = [] } = useListSafetyFunctions(complianceId)

  const { mutate: startRrmGeneration, isPending: isGenerating } =
    useStartRrmGeneration(complianceId)
  const { mutateAsync: exportSrsPdf, isPending: isExportSrsPdfPending } =
    useExportSrsPdf(complianceId)
  const createRiskReductionMeasure = useCreateRiskReductionMeasure(complianceId)
  const updateRiskReductionMeasure = useUpdateRiskReductionMeasure(complianceId)
  const deleteRiskReductionMeasure = useDeleteRiskReductionMeasure(complianceId)

  const isMutationPending =
    createRiskReductionMeasure.isPending ||
    updateRiskReductionMeasure.isPending ||
    deleteRiskReductionMeasure.isPending ||
    isGenerating

  const handleOpenCreate = () => {
    setEditingRiskReductionMeasure(null)
    setIsDialogOpen(true)
  }

  const handleCreate = (data: RiskReductionMeasureFormData) => {
    const payload = toRiskReductionMeasureInput(data)
    createRiskReductionMeasure.mutate(payload, {
      onSuccess: () => {
        toast.success(t('riskReductionMeasuresLibraryPage.createSuccess'))
        setIsDialogOpen(false)
      },
      onError: (err) => {
        toast.error(
          err.message ?? t('riskReductionMeasuresLibraryPage.saveFailureCreate')
        )
      },
    })
  }

  const handleEdit = (riskReductionMeasure: RiskReductionMeasure) => {
    setEditingRiskReductionMeasure(riskReductionMeasure)
    setIsDialogOpen(true)
  }

  const handleUpdate = (
    payload: { riskReductionMeasureId: string } & RiskReductionMeasureFormData
  ) => {
    const { riskReductionMeasureId, ...data } = payload
    updateRiskReductionMeasure.mutate(
      {
        riskReductionMeasureId,
        ...toRiskReductionMeasureInput(data),
      },
      {
        onSuccess: () => {
          toast.success(t('riskReductionMeasuresLibraryPage.updateSuccess'))
          setEditingRiskReductionMeasure(null)
          setIsDialogOpen(false)
        },
        onError: (err) => {
          toast.error(
            err.message ??
              t('riskReductionMeasuresLibraryPage.saveFailureUpdate')
          )
        },
      }
    )
  }

  const handleDeleteClick = (riskReductionMeasureId: string) => {
    setDeletingRiskReductionMeasureId(riskReductionMeasureId)
  }

  const handleAssignClick = (riskReductionMeasure: RiskReductionMeasure) => {
    setAssigningRiskReductionMeasure(riskReductionMeasure)
  }

  const handleAssignSave = (safetyFunctionId: string | null) => {
    if (assigningRiskReductionMeasure == null) {
      return
    }

    updateRiskReductionMeasure.mutate(
      {
        riskReductionMeasureId: assigningRiskReductionMeasure.id,
        ...toSafetyFunctionAssignmentInput(
          assigningRiskReductionMeasure,
          safetyFunctionId
        ),
      },
      {
        onSuccess: () => {
          toast.success(
            t('riskReductionMeasuresLibraryPage.assignDialog.success')
          )
          setAssigningRiskReductionMeasure(null)
        },
        onError: (err) => {
          toast.error(
            err.message ??
              t('riskReductionMeasuresLibraryPage.saveFailureUpdate')
          )
        },
      }
    )
  }

  const handleConfirmDelete = useCallback(async () => {
    if (deletingRiskReductionMeasureId == null) {
      return
    }

    const measureToDelete = riskReductionMeasures.find(
      (measure) => measure.id === deletingRiskReductionMeasureId
    )
    const hasLinkedHazards = (measureToDelete?.linkedHazards?.length ?? 0) > 0
    if (hasLinkedHazards) {
      return
    }

    try {
      await deleteRiskReductionMeasure.mutateAsync(
        deletingRiskReductionMeasureId
      )
      toast.success(t('riskReductionMeasuresLibraryPage.deleteSuccess'))
      setDeletingRiskReductionMeasureId(null)
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : t('riskReductionMeasuresLibraryPage.deleteFailure')
      toast.error(message)
    }
  }, [
    deletingRiskReductionMeasureId,
    deleteRiskReductionMeasure,
    riskReductionMeasures,
    t,
  ])

  const handleExportSrsPdf = useCallback(async () => {
    try {
      await exportSrsPdf()
      toast.success(t('riskReductionMeasuresLibraryPage.exportSrsPdfSuccess'))
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : t('riskReductionMeasuresLibraryPage.exportSrsPdfFailure')
      toast.error(message)
    }
  }, [exportSrsPdf, t])

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-48 w-full' />
      </div>
    )
  }

  const hasError = error != null
  const hasMeasures = riskReductionMeasures.length > 0
  const hasSafetyFunctions = safetyFunctions.length > 0
  const displayError =
    generationError ?? (hasError ? (error as Error).message : null)

  const generatedStandardExtractedMeasureIds = (() => {
    const ids = new Set<string>()
    for (const rrm of riskReductionMeasures) {
      if (
        rrm.source === 'STANDARD' &&
        rrm.standardExtractedMeasureId != null &&
        rrm.standardExtractedMeasureId !== ''
      ) {
        ids.add(rrm.standardExtractedMeasureId)
      }
    }
    return ids
  })()

  const handleGenerateFromStandardsConfirm = (selectedMeasureIds: string[]) => {
    setGenerationError(null)
    setProgressMessage(null)
    setProgressCurrent(null)
    setProgressTotal(null)
    generationProgressCurrentRef.current = null
    generationProgressTotalRef.current = null

    const setAssignEhsrProgressMessage = () => {
      const completedCount = generationProgressCurrentRef.current
      const total = generationProgressTotalRef.current
      const showMeasureProgress = total != null
      if (showMeasureProgress) {
        setProgressMessage(
          t('riskReductionMeasuresLibraryPage.assigningEhsrForMeasure', {
            current: (completedCount ?? 0) + 1,
            total,
          })
        )
        return
      }
      setProgressMessage(t('riskReductionMeasuresLibraryPage.assigningEhsr'))
    }

    startRrmGeneration(
      {
        selectedMeasureIds,
        onEvent: (event: RrmGenerationStreamEvent) => {
          switch (event.type) {
            case 'progress':
              setProgressMessage(event.message)
              break
            case 'rrm.created':
              generationProgressCurrentRef.current = event.rrmIndex
              generationProgressTotalRef.current = event.totalRrms
              setProgressCurrent(event.rrmIndex)
              setProgressTotal(event.totalRrms)
              setProgressMessage(null)
              break
            case 'step.started':
              if (event.step === 'collect_standards_rrms') {
                setProgressMessage(
                  t('riskReductionMeasuresLibraryPage.generating')
                )
              }
              if (event.step === 'get_product_context') {
                setProgressMessage(
                  t('riskReductionMeasuresLibraryPage.loadingProductContext')
                )
              }
              if (event.step === 'assign_ehsr') {
                setAssignEhsrProgressMessage()
              }
              break
            case 'llm.started':
              if (event.step === 'assign_ehsr') {
                setAssignEhsrProgressMessage()
              }
              break
            case 'llm.completed':
              if (event.step === 'assign_ehsr') {
                setAssignEhsrProgressMessage()
              }
              break
            default:
              break
          }
        },
        onComplete: () => {
          setProgressMessage(null)
          setProgressCurrent(null)
          setProgressTotal(null)
          generationProgressCurrentRef.current = null
          generationProgressTotalRef.current = null
          toast.success(
            t('riskReductionMeasuresLibraryPage.generateSuccess')
          )
        },
      },
      {
        onError: (err) => {
          setProgressMessage(null)
          setProgressCurrent(null)
          setProgressTotal(null)
          generationProgressCurrentRef.current = null
          generationProgressTotalRef.current = null
          setGenerationError(err.message)
          toast.error(t('riskReductionMeasuresLibraryPage.generateFailed'))
        },
      }
    )
  }

  return (
    <div className='space-y-4'>
      <ComplianceSubPageHeader className='flex-col items-start sm:flex-col sm:items-start'>
        <ComplianceSubPageTitle>
          {t('riskReductionMeasuresLibraryPage.title')}
        </ComplianceSubPageTitle>
        <div className='flex flex-wrap gap-2'>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant='outline'
                disabled={isMutationPending || !hasSafetyFunctions}
              >
                {isExportSrsPdfPending ? (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                ) : (
                  <Download className='mr-2 size-4' />
                )}
                {t('riskReductionMeasuresLibraryPage.export')}
                <ChevronDown className='ml-2 size-4 opacity-50' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                onClick={handleExportSrsPdf}
                disabled={isExportSrsPdfPending || !hasSafetyFunctions}
              >
                {isExportSrsPdfPending ? (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                ) : (
                  <File className='mr-2 size-4' />
                )}
                {t('riskReductionMeasuresLibraryPage.downloadSrsPdf')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={handleOpenCreate} disabled={isMutationPending}>
            <PlusIcon className='mr-2 size-4' />
            {t('riskReductionMeasuresLibraryPage.addRiskReductionMeasure')}
          </Button>
          <Button
            onClick={() => setIsGenerateModalOpen(true)}
            variant='outline'
            disabled={isGenerating}
          >
            <SparklesIcon className='mr-2 size-4' />
            {t('riskReductionMeasuresLibraryPage.generateFromStandards')}
          </Button>
        </div>
      </ComplianceSubPageHeader>

      {displayError != null && (
        <Alert variant='destructive' className='mb-4'>
          <AlertTitle>
            {t('riskReductionMeasuresLibraryPage.errorTitle')}
          </AlertTitle>
          <AlertDescription>
            {displayError}
          </AlertDescription>
        </Alert>
      )}

      {isGenerating && (
        <Alert className='border-primary/50 bg-primary/5'>
          <AlertTriangle className='size-4' />
          <AlertTitle>{t('riskReductionMeasuresLibraryPage.generating')}</AlertTitle>
          <AlertDescription>
            {progressCurrent != null && progressTotal != null
              ? t('riskReductionMeasuresLibraryPage.generatingMeasure', {
                  current: progressCurrent,
                  total: progressTotal,
                })
              : (progressMessage ?? t('riskReductionMeasuresLibraryPage.generating'))}
          </AlertDescription>
        </Alert>
      )}

      {!displayError && !hasMeasures && (
        <Empty className='min-h-[320px] rounded-lg border border-dashed'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <ShieldCheck className='size-10' />
            </EmptyMedia>
            <EmptyTitle>
              {t('riskReductionMeasuresLibraryPage.emptyState.title')}
            </EmptyTitle>
            <EmptyDescription>
              {t('riskReductionMeasuresLibraryPage.emptyState.description')}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <div className='flex flex-wrap justify-center gap-2'>
              <Button onClick={handleOpenCreate} size='sm'>
                {t('riskReductionMeasuresLibraryPage.emptyState.addFirstMeasure')}
              </Button>
              <Button
                onClick={() => setIsGenerateModalOpen(true)}
                size='sm'
                variant='outline'
              >
                <SparklesIcon className='mr-2 size-4' />
                {t('riskReductionMeasuresLibraryPage.generateFromStandards')}
              </Button>
            </div>
          </EmptyContent>
        </Empty>
      )}

      {!displayError && hasMeasures && (
        <div className='space-y-4'>
          {riskReductionMeasures.map((riskReductionMeasure) => (
            <RiskReductionMeasureCard
              key={riskReductionMeasure.id}
              riskReductionMeasure={riskReductionMeasure}
              safetyFunctions={safetyFunctions}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onAssign={handleAssignClick}
              isMutationPending={isMutationPending}
            />
          ))}
        </div>
      )}

      <RiskReductionMeasureDialog
        key={
          editingRiskReductionMeasure?.id ??
          'create-risk-reduction-measure-dialog'
        }
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false)
          setEditingRiskReductionMeasure(null)
        }}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        riskReductionMeasure={editingRiskReductionMeasure}
        safetyFunctions={safetyFunctions}
        isCreating={createRiskReductionMeasure.isPending}
        isUpdating={updateRiskReductionMeasure.isPending}
      />

      <AssignSafetyFunctionDialog
        isOpen={assigningRiskReductionMeasure != null}
        onClose={() => setAssigningRiskReductionMeasure(null)}
        onSave={handleAssignSave}
        riskReductionMeasure={assigningRiskReductionMeasure}
        safetyFunctions={safetyFunctions}
        isSaving={updateRiskReductionMeasure.isPending}
      />

      <DeleteRiskReductionMeasureDialog
        isOpen={deletingRiskReductionMeasureId != null}
        onClose={() => setDeletingRiskReductionMeasureId(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteRiskReductionMeasure.isPending}
        hasLinkedHazards={
          (riskReductionMeasures.find(
            (measure) => measure.id === deletingRiskReductionMeasureId
          )?.linkedHazards?.length ?? 0) > 0
        }
      />

      <GenerateRrmsFromStandardsModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        complianceId={complianceId}
        onGenerate={handleGenerateFromStandardsConfirm}
        isGenerating={isGenerating}
        generatedStandardExtractedMeasureIds={
          generatedStandardExtractedMeasureIds
        }
      />
    </div>
  )
}
